import type { DataItemId } from '@pn/core/domain/data';
import { UnitSystem } from '@pn/core/domain/types';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { getExtraVisualizedFields } from '@pn/core/domain/workspace';
import { isEmbedded } from '@pn/core/utils/embedded';
import { isEmpty, isNil, isNumber, isString, pick, uniq } from 'lodash-es';

export type Query = {
  id: WorkspaceItem['id'];
  dataType: string;
  page: number;
  limit: number | undefined;
  fields: string[];
  sorts: Sort[];
  filters: Filter[];
  filtersLinkOperator: LinkOperator;
  checkedIds: DataItemId[];
  /**
   * The ids this item is restricted to: a static list's members, a map
   * selection, or the current members of the list referenced by `sourceListId`,
   * which `loadItem` resolves. Filters, SQL and area narrow them further.
   * Empty means no restriction. With a `sourceListId`, API queries send the list
   * reference instead (see `apiQueryMapper`); these ids are only used for
   * counts and local processing.
   */
  scopeIds: DataItemId[];
  /**
   * Features clicked on a base layer. They restrict only that layer's table
   * (see `getScrollableQuery`), not its map, and are never persisted.
   */
  clickedIds: DataItemId[];
  /**
   * Saved list this item stays in sync with. Unlike `scopeIds`, it's what
   * gets persisted, so later changes to the list carry over.
   */
  sourceListId?: WorkspaceItem['id'];
  sql: string | undefined;
  unitSystem: UnitSystem | undefined;
  geometry?: GeoJSON.Geometry;
};

export function buildQuery(
  params: Pick<Query, 'id' | 'dataType'> & Partial<Query>
): Query {
  return {
    page: 0,
    limit: undefined,
    fields: [],
    sorts: [],
    filters: [],
    filtersLinkOperator: 'and',
    checkedIds: [],
    scopeIds: [],
    clickedIds: [],
    sql: undefined,
    unitSystem: undefined,
    ...params,
  };
}

const scrollableQueryKeys = [
  'dataType',
  'page',
  'limit',
  'fields',
  'sorts',
  'filters',
  'filtersLinkOperator',
  'scopeIds',
  'sourceListId',
  'sql',
  'unitSystem',
  'geometry',
] as const;
const streamableQueryKeys = [
  'dataType',
  'limit',
  'fields',
  'sorts',
  'filters',
  'filtersLinkOperator',
  'scopeIds',
  'sourceListId',
  'sql',
  'unitSystem',
  'geometry',
] as const;

export type ScrollableQuery = Pick<
  Query,
  (typeof scrollableQueryKeys)[number]
> & { pageSize: number };
export type StreamableQuery = Pick<Query, (typeof streamableQueryKeys)[number]>;

export const getScrollableQuery = (
  item: WorkspaceItem,
  pageSize: number
): ScrollableQuery => {
  const draftQuery: ScrollableQuery = {
    ...pick(item.query, scrollableQueryKeys),
    pageSize,
  };

  draftQuery.fields = isEmbedded() ? [] : getTableDataFields(item);
  draftQuery.filters = normalizeFilters(item.query.filters);
  if (!isEmpty(item.query.clickedIds)) {
    draftQuery.scopeIds = item.query.clickedIds;
  }

  return draftQuery;
};

export const getExportableQuery = (query: Query): StreamableQuery => {
  const draftQuery: StreamableQuery = pick(query, streamableQueryKeys);

  draftQuery.filters = normalizeFilters(query.filters);

  return draftQuery;
};

export const getVisualizableQuery = (item: WorkspaceItem): StreamableQuery => {
  const draftQuery: StreamableQuery = pick(item.query, streamableQueryKeys);

  draftQuery.fields = getMapDataFields(item); // override fields with only the ones needed for visualization
  draftQuery.filters = normalizeFilters(item.query.filters);

  return draftQuery;
};

export type Sort = {
  field: string;
  direction: 'asc' | 'desc';
};

export type Filter = {
  field: string;
  operator: string;
  value: string | number | boolean | GeoJSON.Geometry | undefined;
};

export type LinkOperator = 'and' | 'or';

export function getMapDataFields(item: WorkspaceItem): string[] {
  return uniq([
    ...(item.dataSource.type === 'api'
      ? item.dataSource.requiredMapDataFields
      : []),
    ...getExtraVisualizedFields(item),
  ]);
}

function getTableDataFields(item: WorkspaceItem): string[] {
  return uniq([
    ...item.query.fields,
    ...(item.dataSource.type === 'api'
      ? item.dataSource.requiredTableDataFields
      : []),
  ]);
}

function normalizeFilters(filters: Filter[]): Filter[] {
  return filters
    .filter((filter) => isValidFilterValue(filter.value))
    .map((filter) => ({
      ...filter,
      value:
        isString(filter.value) && filter.operator === 'contains'
          ? filter.value.toLowerCase().trim()
          : filter.value,
    }));
}

export function areFiltersApplied(filters: Filter[]): boolean {
  return filters.some((filter) => isValidFilterValue(filter.value));
}

function isValidFilterValue(value: Filter['value']): boolean {
  return (
    !isNil(value) && value !== '' && (!isNumber(value) || !Number.isNaN(value))
  );
}
